import React, { createContext, useContext, useState } from 'react'
import { type User } from '@/types/auth'
import { sdk } from './sdk'
import { TEAM_ROLES } from './permissions'
import { trackEvent } from './hooks/useGoogleAnalytics'
import { collectPresence, flushAnalytics, resetAnalyticsSession } from './analytics/collector'
import { type AuthResponse, type HumanCheckProgress, type MeResponse, type SignInData, type SignUpData, isTwoFactorChallenge } from './sdk-official/resources/auth'
import type { TwoFactorChallenge, TwoFactorLoginResult } from './sdk-official/resources/two-factor'
import { readDeviceToken, saveDeviceToken } from './twoFactor/deviceToken'
import { clearStepUpState } from './twoFactor/stepUpBridge'
import { SESSION_ENDED_EVENT } from './sdk-official/internal'
import i18n from './i18n'
import { isAuthGuardFailure } from './authFailure'
import { erasureBlockOf } from './erasure'
import { markFreshLogin } from './push/session'
import { pushLogout } from './push/pushStore'
import { clearBlockDrafts } from './realtime/blockDrafts'

const STORAGE_KEY = '@memberfy/auth'
/** Thrown when the boot's `/auth/me` answers for a session another sign-in already replaced. */
const SUPERSEDED = Symbol('superseded')

interface AuthContextType {
  user: User | null
  /** `onProgress` hears the human check's work level (SEC-173), for a waiting text when it is heavy. */
  /**
   * F-233: with 2FA on, the password step answers the code step (`TwoFactorChallenge`) and no session yet;
   * `completeTwoFactorLogin` ends it. A session answers `null`.
   */
  signIn: (email: string, password?: string, onProgress?: HumanCheckProgress, extra?: Pick<SignInData, 'smsConsent' | 'twoFactorChannel'>) => Promise<TwoFactorChallenge | null>
  /** F-233: the login's code (or a recovery code); with `rememberDevice`, this browser skips the code for 90 days. */
  completeTwoFactorLogin: (email: string, body: { challengeId: string; code?: string; recoveryCode?: string; rememberDevice?: boolean }) => Promise<TwoFactorLoginResult>
  /** F-31 F2: trades the one-time "Entrar" link for the owner's session; failures are rethrown as the SDK raised them. */
  signInWithSupportEntry: (token: string) => Promise<void>
  /** `fields`: the community's signup fields (F-85). A 422 is rethrown as is, so the form can place each `errors[]` under its field. */
  signUp: (email: string, name: string, password: string, fields?: Record<string, string | string[]>, extra?: Pick<SignUpData, 'phone' | 'phoneCountry' | 'smsConsent' | 'ageConfirmed'>) => Promise<void>
  logout: () => void
  isAuthenticated: boolean
  isValidating: boolean
  /**
   * `false` until the role has been read from the member of **this**
   * community. While it is `false`, `hasRole`/`canModerate` answer "no" for
   * everyone but a SuperAdmin: role-gated UI renders nothing (or a skeleton)
   * instead of guessing with the role of another community.
   */
  isRoleResolved: boolean
  /** The session's member in the current community (`/auth/me`); `null` when not known yet or the session is another community's. */
  activeMember: MeResponse | null
  /** Called once the community has been resolved from the domain. */
  setActiveCommunityId: (communityId: string) => void
  hasRole: (roles: string | string[]) => boolean
  canModerate: () => boolean
  /** Keeps the session's own photo current after it is uploaded or removed, `activeMember` included. */
  updateUserAvatar: (avatarUrl: string | null) => void
  mustChangePassword: boolean
  clearMustChangePassword: () => void
  /** Changes this community's password and keeps the session on the new token the API returns (F-31 F3). */
  changePassword: (currentPassword: string, newPassword: string) => Promise<void>
  /**
   * Why the session just ended under the person (the API's `auth.signInAgain`
   * text). The Header opens the login with it, then clears it.
   */
  sessionNotice: string | null
  clearSessionNotice: () => void
  /** Reads `/auth/me` again (F-199: after giving up an erasure, the banner goes away). */
  refreshMe: () => Promise<void>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [isValidating, setIsValidating] = useState(true)
  const [isInitialized, setIsInitialized] = useState(false)
  const [mustChangePassword, setMustChangePassword] = useState(false)
  /* The session's member (`/auth/me`) and the community this app is running
     for (the domain lookup). The role is read from the member only when the
     two match: a session holds in one community (F-31). */
  const [me, setMe] = useState<MeResponse | null>(null)
  const [activeCommunityId, setActiveCommunityIdState] = useState('')
  const [isRoleResolved, setIsRoleResolved] = useState(false)
  const [sessionNotice, setSessionNotice] = useState<string | null>(null)

  const persistSession = (auth: AuthResponse) => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ success: true, data: auth }))
  }

  const readStoredAuth = (): AuthResponse | null => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY)
      if (!stored) return null
      const parsed = JSON.parse(stored) as { data?: AuthResponse }
      return parsed?.data ?? null
    } catch {
      return null
    }
  }

  const buildUser = (member: AuthResponse['member']): User => ({
    id: member.id,
    email: member.email,
    name: member.fullName,
    isSuperAdmin: !!member.isSuperAdmin,
    role: member.role || 'member',
    avatar: member.avatarUrl
  })

  /** The fields of a failed SDK call this context reads: the status and the server's message. */
  type ApiFailure = { status?: number; message?: string; response?: { status?: number; data?: { message?: string } } } | null | undefined

  const apiMessage = (error: ApiFailure, fallback: string): string =>
    error?.response?.data?.message || error?.message || fallback

  /* A request with the session token came back 401 (F-31 F3: a token from
     before the per-community login, from another community, or one a password
     change replaced). The session is over: clear it and let the Header open the
     login with the API's message. No navigation, so nothing can loop. */
  React.useEffect(() => {
    const onSessionEnded = (event: Event) => {
      const message = (event as CustomEvent<{ message?: string }>).detail?.message
      /* F-233: a step-up token never outlives the session that earned it. */
      clearStepUpState()
      sdk.setToken('')
      sdk.setMemberId('')
      setMe(null)
      setIsRoleResolved(false)
      setUser(null)
      setMustChangePassword(false)
      localStorage.removeItem(STORAGE_KEY)
      /* F-174: a block's unsaved text never outlives the session on this device. */
      clearBlockDrafts()
      setSessionNotice(message || i18n.t('auth:sessionEnded'))
    }
    window.addEventListener(SESSION_ENDED_EVENT, onSessionEnded)
    return () => window.removeEventListener(SESSION_ENDED_EVENT, onSessionEnded)
  }, [])

  React.useEffect(() => {
    const initializeAuth = async () => {
      setIsValidating(true)

      const stored = readStoredAuth()

      if (stored) {
        sdk.setToken(stored.token)
        /* The stored role is not trusted until it is resolved for this
           community: screens that read `user.role` directly, without
           `hasRole`, would otherwise flash the UI of another role. */
        setUser(buildUser({ ...stored.member, role: 'member' }))
        setMustChangePassword(!!stored.mustChangePassword)

        try {
          /* Sent with this community's X-CommunityId (the effect waits for
             it): `isSuperAdmin` is the answer for this community, true only
             in the platform ("admin") community (F-31 F2). */
          const current: MeResponse = await sdk.auth.me()
          /* Another sign-in took over meanwhile (the "Entrar" link signs in
             on load): this answer is about the session it replaced. */
          if (readStoredAuth()?.token !== stored.token) throw SUPERSEDED

          /* The role is NOT set here: it is read in the effect below, once
             the community is known too, and only from a member of that
             community (APP-37). */
          setUser(buildUser(
            {
              id: current.id,
              fullName: current.fullName,
              email: current.email,
              avatarUrl: current.avatarUrl,
              /* From the server on every load (APP-39). The stored value is
                 only the first paint; this is the one that counts. */
              isSuperAdmin: !!current.isSuperAdmin,
              role: 'member'
            }
          ))
          persistSession({ ...stored, member: { ...stored.member, isSuperAdmin: !!current.isSuperAdmin } })
          setMe(current)
          // On API failure (network or server down), the stored session is
          // kept so a temporary outage does not log the user out.
        } catch (caught) {
          /* A superseded session's failure is not the new one's: nothing to undo. */
          const superseded = caught === SUPERSEDED || readStoredAuth()?.token !== stored.token
          if (!superseded && ((caught as ApiFailure)?.status === 401 || (caught as ApiFailure)?.response?.status === 401)) {
            sdk.setToken('')
            setUser(null)
            setMustChangePassword(false)
            localStorage.removeItem(STORAGE_KEY)
            clearBlockDrafts()
          }
        }
      }

      setIsValidating(false)
      setIsInitialized(true)
    }

    /* Waits for the domain lookup: `/auth/me` without X-CommunityId answers
       `isSuperAdmin: false`, and the platform community's SuperAdmin would
       lose the admin screens on every reload (F-31 F2). */
    if (!isInitialized && activeCommunityId) {
      initializeAuth()
    }
  }, [isInitialized, activeCommunityId])

  /* Reads the role once both halves are in: the session's member and the
     community resolved from the domain. Until then `isRoleResolved` stays
     `false` and role-gated UI stays closed. */
  React.useEffect(() => {
    if (!user?.id || me === null || !activeCommunityId) return

    const member = me.communityId === activeCommunityId ? me : null
    const role = member?.role || 'member'

    setUser(previous => (previous ? { ...previous, role } : previous))
    setIsRoleResolved(true)

    /* `X-MemberId` means "act as this member", and only a SuperAdmin can do
       that — it is the member picker's header, set by the picker and by
       nothing else (APP-31). A stored id is kept only when it was picked in
       this community, so a browser carrying a bad value repairs itself on the
       next load. */
    const storedMemberId = sdk.getCurrentMember()
    if (storedMemberId && !(user.isSuperAdmin && sdk.getCurrentMemberCommunity() === activeCommunityId)) {
      sdk.setMemberId('')
    }

    const stored = readStoredAuth()
    if (stored) {
      persistSession({ ...stored, member: { ...stored.member, role } })
    }
    /* `user?.id` and `user?.isSuperAdmin` only: the effect writes `user`
       itself. The flag is set before `me`, in the same boot step. */
  }, [me, activeCommunityId, user?.id, user?.isSuperAdmin])

  /** The member of a session that just started (sign-in/sign-up), so the role can be resolved. */
  const loadMe = async () => {
    try {
      setMe(await sdk.auth.me())
    } catch {
      /* Without it the role stays unresolved: the person is treated as a
         member until the next load, which is the safe side. */
    }
  }

  const resetRole = () => {
    setMe(null)
    setIsRoleResolved(false)
  }

  const setActiveCommunityId = React.useCallback((communityId: string) => {
    /* F-233: another community's protected actions ask for their own step-up. */
    setActiveCommunityIdState((current) => {
      const next = communityId || ''
      if (next !== current) clearStepUpState()
      return next
    })
  }, [])

  const signUp = async (email: string, name: string, password: string, fields?: Record<string, string | string[]>, extra?: Pick<SignUpData, 'phone' | 'phoneCountry' | 'smsConsent' | 'ageConfirmed'>) => {
    try {
      const auth = await sdk.auth.signUp({ email, name, password, fields, ...extra })
      clearStepUpState()
      sdk.setToken(auth.token)
      // GA: the community's own analytics; only the method, never who.
      trackEvent('sign_up', { method: 'email' })
      setSessionNotice(null)
      resetRole()
      setUser(buildUser(auth.member))
      persistSession(auth)
      /* F-186: right after logging in, the install / notifications modal is due once. */
      markFreshLogin()
      collectPresence()
      void loadMe()

      if (auth.mustChangePassword) {
        setMustChangePassword(true)
      }
    } catch (caught) {
      const error = caught as ApiFailure
      const status = error?.status ?? error?.response?.status
      if (status === 422 || isAuthGuardFailure(caught)) {
        throw caught
      } else if (status === 401) {
        throw new Error('Invalid email or password')
      } else if (status === 409) {
        throw new Error('This email is already registered')
      } else if (status === 400) {
        throw new Error(apiMessage(error, 'Invalid request. Please check your information.'))
      } else {
        throw new Error(apiMessage(error, 'An unexpected error occurred. Please try again later.'))
      }
    }
  }

  /** The session of a login that passed every step. */
  const startSession = (auth: AuthResponse) => {
    /* What this tab collected while logged out leaves as anonymous; the API then ties the session to the member (F-88). */
    flushAnalytics()
    /* F-233: whatever step-up the previous person earned in this tab is gone. */
    clearStepUpState()
    sdk.setToken(auth.token)
    trackEvent('login', { method: 'email' })
    setSessionNotice(null)
    resetRole()
    setUser(buildUser(auth.member))
    persistSession({ member: auth.member, token: auth.token, mustChangePassword: auth.mustChangePassword })
    /* F-186: right after logging in, the install / notifications modal is due once. */
    markFreshLogin()
    collectPresence()
    void loadMe()

    if (auth.mustChangePassword) {
      setMustChangePassword(true)
    }
  }

  const signInFailure = (caught: unknown): never => {
    const error = caught as ApiFailure
    const status = error?.status ?? error?.response?.status
    /* F-199: the erasure's 403 goes through as raised, so the login can offer to give up. */
    if (isAuthGuardFailure(caught) || erasureBlockOf(caught)) {
      throw caught
    } else if (status === 401) {
      throw new Error('Invalid email or password')
    } else {
      throw new Error(apiMessage(error, 'An unexpected error occurred. Please try again later.'))
    }
  }

  const signIn = async (email: string, password?: string, onProgress?: HumanCheckProgress, extra?: Pick<SignInData, 'smsConsent' | 'twoFactorChannel'>): Promise<TwoFactorChallenge | null> => {
    try {
      const deviceToken = readDeviceToken(sdk.getCommunityId(), email)
      const answer = await sdk.auth.signIn({ email, password, ...extra, ...(deviceToken ? { deviceToken } : {}) }, onProgress)
      /* F-233: the code step comes first; no session until it passes. */
      if (isTwoFactorChallenge(answer)) return answer
      startSession(answer)
      return null
    } catch (caught) {
      return signInFailure(caught)
    }
  }

  /* Errors go through as raised: the code step reads `param`, `data.attemptsLeft` and the status itself. */
  const completeTwoFactorLogin = async (email: string, body: { challengeId: string; code?: string; recoveryCode?: string; rememberDevice?: boolean }) => {
    const result = await sdk.twoFactor.verifyLogin(body)
    if (result.deviceToken) saveDeviceToken(sdk.getCommunityId(), email, result.deviceToken)
    startSession(result)
    return result
  }

  const signInWithSupportEntry = async (token: string) => {
    const auth = await sdk.auth.supportEntry(token)
    flushAnalytics()
    clearStepUpState()
    sdk.setToken(auth.token)
    /* Whatever "act as" pick this browser had belongs to another session. */
    sdk.setMemberId('')
    setSessionNotice(null)
    resetRole()
    setUser(buildUser(auth.member))
    persistSession(auth)
    collectPresence()
    setMustChangePassword(!!auth.mustChangePassword)
    void loadMe()
  }

  const logout = () => {
    /* A new analytics session after logout: nothing collected next is tied to who left (F-88). */
    resetAnalyticsSession()
    /* F-186: this device stops getting this person's push; the token is read before it goes. */
    pushLogout(localStorage.getItem('memberfy_auth_token'), sdk.getCommunityId())
    clearStepUpState()
    sdk.setToken('')
    sdk.setMemberId('')
    resetRole()
    setUser(null)
    setMustChangePassword(false)
    localStorage.removeItem(STORAGE_KEY)
    clearBlockDrafts()
  }

  /**
   * Clears the forced-change flag in **storage** too, not only in React state.
   *
   * `persistSession` writes the whole login response, `mustChangePassword`
   * included, and the boot effect reads it back from there. Clearing only the
   * state closed the dialog for the current render and the next reload put it
   * straight back — with no way to dismiss it, since a forced change has no
   * close button. Changing the password did not help: the flag it reads lives
   * in localStorage, and nothing was writing to it.
   */
  const clearMustChangePassword = () => {
    setMustChangePassword(false)
    try {
      const stored = readStoredAuth()
      if (stored) persistSession({ ...stored, mustChangePassword: false })
    } catch {
      /* Storage unavailable. The state is already clear, so this session is
         usable — the dialog would only come back on a reload. */
    }
  }

  const clearSessionNotice = React.useCallback(() => setSessionNotice(null), [])

  const changePassword = async (currentPassword: string, newPassword: string) => {
    const result = await sdk.auth.changePassword({ currentPassword, newPassword })
    /* The change bumps the account's token version: the token that asked is
       already dead, and without the new one the next request would end the session. */
    if (result?.token) {
      sdk.setToken(result.token)
      const stored = readStoredAuth()
      if (stored) persistSession({ ...stored, token: result.token })
    }
  }

  const hasRole = (roles: string | string[]) => {
    if (!user) return false
    if (user.isSuperAdmin) return true
    /* Unknown role means "not authorized", not "whatever the session last
       said": the stored role may belong to another community. */
    if (!isRoleResolved) return false
    const roleArray = Array.isArray(roles) ? roles : [roles]
    return roleArray.includes(user.role)
  }

  const canModerate = () => {
    return hasRole(TEAM_ROLES) || user?.isSuperAdmin || false
  }

  const updateUserAvatar = (avatarUrl: string | null) => {
    if (!user) return

    setUser({
      ...user,
      avatar: avatarUrl
    })
    setMe(previous => (previous ? { ...previous, avatarUrl } : previous))

    try {
      const stored = readStoredAuth()
      if (stored) {
        stored.member.avatarUrl = avatarUrl
        persistSession(stored)
      }
    } catch (error) {
      console.error('[AuthContext] Error updating avatar in localStorage:', error)
    }
  }

  const value = {
    user,
    signUp,
    signIn,
    completeTwoFactorLogin,
    signInWithSupportEntry,
    logout,
    isAuthenticated: !!user,
    isValidating,
    isRoleResolved,
    activeMember: isRoleResolved && me && me.communityId === activeCommunityId ? me : null,
    setActiveCommunityId,
    hasRole,
    canModerate,
    updateUserAvatar,
    mustChangePassword,
    clearMustChangePassword,
    changePassword,
    sessionNotice,
    clearSessionNotice,
    refreshMe: loadMe
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
